/**
 * SunoVideoJobsContext — фоновые music-video генерации.
 *
 * Источник истины — backend (track.video_url в /tracks). localStorage НЕ
 * используется для хранения результатов — только pending-jobs для
 * реставрации polling'а после reload (UI-state).
 *
 * На успешное завершение диспатчится window-event `suno-video-ready` —
 * слушатели перезапрашивают /tracks чтобы бейдж обновился.
 *
 * Session-cache (useState, живёт до reload) — для быстрого повторного
 * открытия viewer'а в пределах одной сессии.
 *
 * API:
 *   - start(track)       — запустить
 *   - isRunning(id)      — идёт ли polling
 *   - getCached(id)      — session-cache (null если в этой сессии не смотрели)
 *   - pendingViewer      — если готов — данные для viewer'а
 *   - dismissViewer()    — закрыть viewer
 *   - openCached(track)  — sync открыть из session-кэша
 *   - openFromBackend()  — async open с lazy-fetch (track.video_url первый,
 *                          fallback getTrackVideo)
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { createMusicVideo, getMurekaClipStatus, getTrackVideo, type ClipAspect, type ClipLayout, type SunoTrack } from '../api/music'
import { TASK_GONE_MESSAGE, type TaskStatusResponse } from '../api/images'
import { pollUntil, waitForTask } from '../pages/generate/utils/waitForTask'
import { showToast } from '../components/Toast'
import { translateSunoError } from '../pages/generate/utils/sunoErrors'

const LS_PENDING_KEY = 'suno_video_pending_v1'
const POLL_INTERVAL_MS = 10_000

// «Клип по треку» (Mureka Music Video/Seedance) — отдельная ветка задач: свой
// эндпоинт статуса (clip-status по task_id провайдера, не request_id) и свой
// ключ pending — иначе резюме после F5 пыталось бы опросить чужой стейт.
const LS_CLIP_KEY = 'mureka_clip_pending_v1'
const CLIP_POLL_INTERVAL_MS = 5_000

interface PendingVideoJob {
	trackId: string
	requestId: string
	title: string | null
	startedAt: number
}

interface PendingClipJob {
	trackId: string
	taskId: string
	title: string | null
	startedAt: number
}

interface CachedVideo {
	trackId: string
	title: string | null
	videoUrl: string
	createdAt: number
}

export interface VideoViewerState {
	trackId: string
	title: string | null
	videoUrl: string
	imageUrl: string | null
}

/** Опции караоке-видео Mureka: формат кадра + оформление + ставить ли обложку.
 *  У Suno игнорируются — бэк кладёт их в extra_params, до Suno-адаптера они не
 *  доходят (иначе был бы TypeError на спреде adapter_kwargs). */
export interface VideoStartOptions {
	aspect_ratio?: ClipAspect
	layout?: ClipLayout
	use_cover?: boolean
}

interface SunoVideoJobsValue {
	start: (track: SunoTrack, opts?: VideoStartOptions) => void
	isRunning: (trackId: string) => boolean
	getCached: (trackId: string) => string | null
	pendingViewer: VideoViewerState | null
	dismissViewer: () => void
	openCached: (track: SunoTrack) => boolean
	/** Открыть конкретный клип по URL (у трека их может быть несколько). */
	openUrl: (track: SunoTrack, videoUrl: string) => void
	/** Открыть viewer из бэка: если у трека есть track.video_url (из /tracks)
	 *  — open напрямую без лишних запросов. Иначе fallback на getTrackVideo. */
	openFromBackend: (track: SunoTrack) => Promise<boolean>
	/** Следить за уже созданной задачей «Клип по треку»: пилюля на строке,
	 *  глобальный поллинг, тост и viewer по готовности. silent — без
	 *  стартового тоста (резюме после перезагрузки). */
	watchClip: (track: SunoTrack, taskId: string, opts?: { silent?: boolean }) => void
	/** Идёт ли клип этого трека (кнопка «Клип…» и пилюля на строке). */
	isClipRunning: (trackId: string) => boolean
}

const SunoVideoJobsContext = createContext<SunoVideoJobsValue | null>(null)

// ─── localStorage helpers (ТОЛЬКО pending-jobs, не данные) ─────────
function loadPending(): Record<string, PendingVideoJob> {
	try {
		const raw = localStorage.getItem(LS_PENDING_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		const filtered: Record<string, PendingVideoJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingVideoJob>)) {
			if (v && typeof v === 'object' && v.requestId && v.startedAt) {
				// Запуск с номером задачи живёт, пока сервер не ответил (план «Ждём ответа нейросети», Ф3): прежние
				// 30 минут бросали следить за долгой задачей после перезагрузки.
				filtered[k] = v
			}
		}
		return filtered
	} catch {
		return {}
	}
}
function savePending(pending: Record<string, PendingVideoJob>) {
	try {
		localStorage.setItem(LS_PENDING_KEY, JSON.stringify(pending))
	} catch {
		// localStorage недоступен (приватный режим/квота) — pending не
		// сохраняем: поллинг доживёт в памяти до перезагрузки страницы.
	}
}

function loadPendingClips(): Record<string, PendingClipJob> {
	try {
		const raw = localStorage.getItem(LS_CLIP_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		const filtered: Record<string, PendingClipJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingClipJob>)) {
			if (v && typeof v === 'object' && v.taskId && v.startedAt) {
				// Клип с номером задачи живёт, пока сервер не ответил: сдаётся он сам, на третьи сутки (music/process).
				filtered[k] = v
			}
		}
		return filtered
	} catch {
		return {}
	}
}
function savePendingClips(pending: Record<string, PendingClipJob>) {
	try {
		localStorage.setItem(LS_CLIP_KEY, JSON.stringify(pending))
	} catch {
		// см. savePending
	}
}

function extractVideoUrl(s: TaskStatusResponse): string | null {
	if (s.result_url) return s.result_url
	if (Array.isArray(s.result_urls) && s.result_urls[0]) return s.result_urls[0]
	return null
}

export function SunoVideoJobsProvider({ children }: { children: ReactNode }) {
	// In-memory session cache — не persistent. Для бейджа на строке трека
	// источник истины — track.video_url из /tracks.
	const [cache, setCache] = useState<Record<string, CachedVideo>>({})
	const [runningIds, setRunningIds] = useState<Set<string>>(
		() => new Set(Object.keys(loadPending())),
	)
	const [pendingViewer, setPendingViewer] = useState<VideoViewerState | null>(null)
	const activeRef = useRef<Set<string>>(new Set())
	const pendingRef = useRef<Record<string, PendingVideoJob>>(loadPending())
	// Ключ pending — taskId (не trackId): у одного трека может идти НЕСКОЛЬКО
	// сцен сразу (лента не закрывается при запуске). runningIds — трек-уровень,
	// пересчитывается из живых задач.
	const [clipRunningIds, setClipRunningIds] = useState<Set<string>>(
		() => new Set(Object.values(loadPendingClips()).map((j) => j.trackId)),
	)
	const clipActiveRef = useRef<Set<string>>(new Set())
	const clipPendingRef = useRef<Record<string, PendingClipJob>>(loadPendingClips())

	const writeCache = useCallback(
		(trackId: string, title: string | null, videoUrl: string) => {
			setCache((prev) => ({
				...prev,
				[trackId]: { trackId, title, videoUrl, createdAt: Date.now() },
			}))
		},
		[],
	)

	const markRunning = useCallback((trackId: string, running: boolean) => {
		setRunningIds((prev) => {
			const next = new Set(prev)
			if (running) next.add(trackId)
			else next.delete(trackId)
			return next
		})
	}, [])

	const setPendingJob = useCallback((trackId: string, job: PendingVideoJob | null) => {
		if (job) pendingRef.current[trackId] = job
		else delete pendingRef.current[trackId]
		savePending({ ...pendingRef.current })
	}, [])

	const publishViewer = useCallback((state: VideoViewerState | null) => {
		setPendingViewer(state)
	}, [])

	const runPolling = useCallback(
		async (trackId: string, requestId: string, title: string | null, imageUrl: string | null) => {
			if (activeRef.current.has(trackId)) return
			activeRef.current.add(trackId)
			markRunning(trackId, true)
			setPendingJob(trackId, {
				trackId,
				requestId,
				title,
				startedAt: pendingRef.current[trackId]?.startedAt ?? Date.now(),
			})

			try {
				// Ждём ответа сервера без потолка (waitForTask, план «Ждём ответа нейросети», Ф3).
				const s = await waitForTask(requestId, {
					minDelayMs: POLL_INTERVAL_MS,
					startedAt: pendingRef.current[trackId]?.startedAt,
				})
				if (!s) return
				if (s.status === 'failed') {
					showToast(translateSunoError(s.error_message_ru || s.error_message, 'video'), 'error')
					return
				}
				const videoUrl = extractVideoUrl(s)
				if (!videoUrl) {
					showToast('Suno завершил задачу без ролика — попробуй ещё раз.', 'error')
					return
				}

				writeCache(trackId, title, videoUrl)
				showToast(`Видеоклип для «${title?.trim() || 'трека'}» готов`, 'success')
				publishViewer({ trackId, title, videoUrl, imageUrl })
				// /tracks теперь должен возвращать video_url на этом треке —
				// уведомляем подписчиков чтобы бейдж 🎬 появился на строке.
				window.dispatchEvent(
					new CustomEvent('suno-video-ready', { detail: { trackId } }),
				)
			} finally {
				activeRef.current.delete(trackId)
				markRunning(trackId, false)
				setPendingJob(trackId, null)
			}
		},
		[markRunning, publishViewer, setPendingJob, writeCache],
	)

	const start = useCallback(
		async (track: SunoTrack, opts?: VideoStartOptions) => {
			const trackId = track.id

			// Есть кэш — сразу открываем viewer.
			const cached = cache[trackId]
			if (cached) {
				publishViewer({
					trackId,
					title: track.title,
					videoUrl: cached.videoUrl,
					imageUrl: track.image_url || null,
				})
				return
			}

			if (activeRef.current.has(trackId)) {
				showToast('Видеоклип уже генерируется...', 'info')
				return
			}

			showToast(
				`Создаём видеоклип для «${track.title?.trim() || 'трека'}»... Это займёт 3-5 минут, можешь спокойно закрыть окно — мы уведомим когда готов.`,
				'info',
			)

			const res = await createMusicVideo({ track_id: trackId, ...(opts || {}) })
			if (!res.ok) {
				showToast(translateSunoError(res.error, 'video'), 'error')
				return
			}
			if (!res.request_id) {
				showToast('Не удалось запустить генерацию видеоклипа', 'error')
				return
			}
			runPolling(trackId, res.request_id, track.title, track.image_url || null)
		},
		[cache, publishViewer, runPolling],
	)

	const setPendingClip = useCallback((taskId: string, job: PendingClipJob | null) => {
		if (job) clipPendingRef.current[taskId] = job
		else delete clipPendingRef.current[taskId]
		savePendingClips({ ...clipPendingRef.current })
		// Трек «идёт», пока жива хоть одна его задача.
		setClipRunningIds(new Set(Object.values(clipPendingRef.current).map((j) => j.trackId)))
	}, [])

	const watchClip = useCallback(
		(track: SunoTrack, taskId: string, opts?: { silent?: boolean }) => {
			const trackId = track.id
			if (!taskId || clipActiveRef.current.has(taskId)) return
			clipActiveRef.current.add(taskId)
			setPendingClip(taskId, {
				trackId,
				taskId,
				title: track.title,
				startedAt: clipPendingRef.current[taskId]?.startedAt ?? Date.now(),
			})
			if (!opts?.silent) {
				showToast('Клип генерируется — обычно 1–2 минуты. Можно уходить со страницы: мы скажем, когда готов.', 'info')
			}

			void (async () => {
				try {
					// Ждём ответа Mureka без потолка (pollUntil, план «Ждём ответа нейросети», Ф3): статус клипа сервер
					// сам сверяет с Mureka и закрывает клип её ответом.
					const st = await pollUntil(
						async () => {
							const r = await getMurekaClipStatus(taskId)
							const done = (r.status === 'succeeded' && !!r.video_url)
								|| r.ok === false || ['failed', 'cancelled', 'expired'].includes(r.status || '')
							return done ? r : undefined
						},
						{
							minDelayMs: CLIP_POLL_INTERVAL_MS,
							startedAt: clipPendingRef.current[taskId]?.startedAt,
							onGone: () => ({ ok: false, status: 'failed', error: TASK_GONE_MESSAGE }),
						},
					)
					if (st?.status === 'succeeded' && st.video_url) {
						showToast(`Клип для «${track.title?.trim() || 'трека'}» готов`, 'success')
						publishViewer({ trackId, title: track.title, videoUrl: st.video_url, imageUrl: track.image_url || null })
						// Кабинет трека перезагрузит карту клипов на дорожке.
						window.dispatchEvent(new CustomEvent('mureka-clip-ready', { detail: { trackId } }))
					} else if (st) {
						showToast(st.error || 'Клип не получился — токены возвращены', 'error')
						window.dispatchEvent(new CustomEvent('mureka-clip-ready', { detail: { trackId } }))
					}
				} finally {
					clipActiveRef.current.delete(taskId)
					setPendingClip(taskId, null)
				}
			})()
		},
		[publishViewer, setPendingClip],
	)

	const isClipRunning = useCallback((trackId: string) => clipRunningIds.has(trackId), [clipRunningIds])

	const openCached = useCallback(
		(track: SunoTrack): boolean => {
			const cached = cache[track.id]
			if (!cached) return false
			publishViewer({
				trackId: track.id,
				title: track.title,
				videoUrl: cached.videoUrl,
				imageUrl: track.image_url || null,
			})
			return true
		},
		[cache, publishViewer],
	)

	// Открыть КОНКРЕТНЫЙ клип трека (у трека их может быть несколько — по
	// одному на сделанное оформление; пикер открывает выбранный).
	const openUrl = useCallback(
		(track: SunoTrack, videoUrl: string) => {
			publishViewer({
				trackId: track.id,
				title: track.title,
				videoUrl,
				imageUrl: track.image_url || null,
			})
		},
		[publishViewer],
	)

	const openFromBackend = useCallback(
		async (track: SunoTrack): Promise<boolean> => {
			// track.video_url приходит в /tracks response — если есть, открываем
			// сразу без дополнительного сетевого запроса.
			if (track.video_url) {
				publishViewer({
					trackId: track.id,
					title: track.title,
					videoUrl: track.video_url,
					imageUrl: track.image_url || null,
				})
				return true
			}
			// Локальный кэш
			if (openCached(track)) return true
			// Fallback: отдельный endpoint (обычно не нужен, video_url уже в /tracks)
			try {
				const res = await getTrackVideo(track.id)
				if (!res.ok || !res.video_url) return false
				publishViewer({
					trackId: track.id,
					title: track.title,
					videoUrl: res.video_url,
					imageUrl: track.image_url || null,
				})
				return true
			} catch {
				return false
			}
		},
		[openCached, publishViewer],
	)

	const getCached = useCallback(
		(trackId: string): string | null => cache[trackId]?.videoUrl ?? null,
		[cache],
	)

	const dismissViewer = useCallback(() => {
		publishViewer(null)
	}, [publishViewer])

	// Restore pending после reload
	useEffect(() => {
		const pending = pendingRef.current
		const ids = Object.keys(pending)
		if (ids.length === 0) return
		const t = setTimeout(() => {
			for (const trackId of ids) {
				const job = pending[trackId]
				if (!job) continue
				runPolling(trackId, job.requestId, job.title, null)
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Restore идущих клипов после reload: пилюля на строке и поллинг живут
	// без открытого кабинета. silent — стартовый тост уже был при запуске.
	useEffect(() => {
		const pending = clipPendingRef.current
		const keys = Object.keys(pending)
		if (keys.length === 0) return
		const t = setTimeout(() => {
			for (const key of keys) {
				const job = pending[key]
				if (!job) continue
				// Миграция старого формата (ключом был trackId): перекладываем
				// под taskId, иначе завершение задачи не удалит запись.
				if (key !== job.taskId) {
					delete pending[key]
					pending[job.taskId] = job
				}
				clipActiveRef.current.delete(job.taskId)
				watchClip({ id: job.trackId, title: job.title, image_url: null } as SunoTrack, job.taskId, { silent: true })
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const isRunning = useCallback((trackId: string) => runningIds.has(trackId), [runningIds])

	const value = useMemo<SunoVideoJobsValue>(
		() => ({ start, isRunning, getCached, pendingViewer, dismissViewer, openCached, openUrl, openFromBackend, watchClip, isClipRunning }),
		[start, isRunning, getCached, pendingViewer, dismissViewer, openCached, openUrl, openFromBackend, watchClip, isClipRunning],
	)

	return (
		<SunoVideoJobsContext.Provider value={value}>{children}</SunoVideoJobsContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSunoVideoJobs(): SunoVideoJobsValue {
	const ctx = useContext(SunoVideoJobsContext)
	if (!ctx) {
		return {
			start: () => {},
			isRunning: () => false,
			getCached: () => null,
			pendingViewer: null,
			dismissViewer: () => {},
			openCached: () => false,
			openUrl: () => {},
			openFromBackend: async () => false,
			watchClip: () => {},
			isClipRunning: () => false,
		}
	}
	return ctx
}
